import type React from 'react'
import { useDocumentTitle } from '@/hooks/document-title'
import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormContainer } from '@/components/FormContainer'
import { useId } from 'react'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { StyledLink } from '@/components/Link'
import styles from './RequestMagicLinkView.module.css'
import type { RequestMagicLinkViewModel } from './RequestMagicLinkViewModel'
import type { PreselectedWorkspaceInfo } from '../../workspaces/preselected-workspace-info'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { formatUSPhoneNumber } from '@/utils/phone-number-util'
import { useTranslate } from '@/hooks/translate'

/**
 * The RequestMagicLinkView view
 */
export default observer(function RequestMagicLinkView() {
  useDocumentTitle('Request Magic Link')

  const { requestMagicLinkVm, readPreselectedWorkspaceInfo } = useFeatures(
    ({ iam }) => ({
      requestMagicLinkVm: iam.requestMagicLinkVm,
      readPreselectedWorkspaceInfo: iam.readPreselectedWorkspaceInfo,
    }),
  )

  const preselectedWorkspaceInfo = readPreselectedWorkspaceInfo()
  useActivation(requestMagicLinkVm)

  return (
    <div className={styles.container}>
      <RequestMagicLinkContainer
        vm={requestMagicLinkVm}
        preselectedWorkspaceInfo={preselectedWorkspaceInfo}
      />
    </div>
  )
})

interface RequestMagicLinkContainerProps {
  vm: RequestMagicLinkViewModel
  preselectedWorkspaceInfo: PreselectedWorkspaceInfo | null
}

/**
 * The RequestMagicLinkContainer container
 */
const RequestMagicLinkContainer = observer(
  ({ vm, preselectedWorkspaceInfo }: RequestMagicLinkContainerProps) => {
    const formId = useId()
    const lightDark = useLightDarkFn()
    const callCenterConfig =
      window.__workspaceDomain?.workspace?.config?.call_center_config
    const T = useTranslate()
    const callCenterNumber = callCenterConfig?.enabled
      ? callCenterConfig?.phone_number
      : undefined
    const callCenterHours = callCenterNumber
      ? T('Web.CallCenterHours', 'Monday - Friday, 9am - 5pm EST')
      : undefined

    return (
      <div className={styles.container}>
        {!vm.submitted ? (
          <RequestMagicLink
            formId={formId}
            logo={
              preselectedWorkspaceInfo
                ? lightDark(
                    preselectedWorkspaceInfo.primaryLogoUrl,
                    preselectedWorkspaceInfo.invertedPrimaryLogoUrl,
                  )
                : ''
            }
            isPending={vm.sendMagicLink.pending}
            callCenterNumber={callCenterNumber}
            callCenterHours={callCenterHours}
          >
            <FormContainer
              compact
              id={formId}
              onSubmit={() =>
                vm.sendMagicLink(preselectedWorkspaceInfo?.id, vm.email)
              }
            >
              <TextFieldContainer
                model={vm}
                field="email"
                name="email"
                label="Email address"
                placeholder="example@email.com"
                disabled={vm.sendMagicLink.pending}
              />
            </FormContainer>
          </RequestMagicLink>
        ) : (
          <MagicLinkSent
            email={vm.email}
            logo={
              preselectedWorkspaceInfo
                ? lightDark(
                    preselectedWorkspaceInfo.primaryLogoUrl,
                    preselectedWorkspaceInfo.invertedPrimaryLogoUrl,
                  )
                : ''
            }
            reset={vm.reset}
          />
        )}
      </div>
    )
  },
)

interface RequestMagicLinkProps {
  formId: string
  logo: string
  isPending: boolean
  children: React.ReactNode
  testId?: string
  submitButtonTestId?: string
  callCenterNumber?: string
  callCenterHours?: string
}

/**
 * The RequestMagicLink component
 */
export function RequestMagicLink({
  formId,
  logo,
  children,
  isPending,
  testId,
  submitButtonTestId,
  callCenterNumber,
  callCenterHours,
}: RequestMagicLinkProps) {
  return (
    <div className={styles.content} data-test={testId}>
      <div className={styles.header}>
        <Header
          title="Access your account"
          description="Enter your email address to request access to your dashboard"
          logo={logo}
          callCenterNumber={callCenterNumber}
          callCenterHours={callCenterHours}
        />
      </div>

      <div className={styles.body}>{children}</div>

      <div className={styles.footer}>
        <ButtonGroup stretch>
          <Button
            type="submit"
            label="Request access"
            form={formId}
            isPending={isPending}
            testId={submitButtonTestId}
          />
        </ButtonGroup>
      </div>
    </div>
  )
}

interface MagicLinkSentProps {
  email: string
  logo: string
  reset: () => void
}

/**
 * The MagicLinkSent component
 */
export function MagicLinkSent({ email, logo, reset }: MagicLinkSentProps) {
  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Header
          title="Check your inbox to see your access link"
          description={
            <>
              We’ve sent a secure access link to <b>{email}</b>.
              <br />
              <br />
              The link will expire in 15 minutes for security.
            </>
          }
          logo={logo}
        />
        <div className={styles.box}>
          <h2 className={styles.boxTitle}>Didn’t receive the email?</h2>
          <div className={styles.boxContent}>
            Check your spam folder or <br />
            <StyledLink onPress={reset}>send the email again</StyledLink>
          </div>
        </div>
      </div>
    </div>
  )
}

interface HeaderProps {
  title: string
  description: React.ReactNode
  logo: string
  callCenterNumber?: string
  callCenterHours?: string
}

/**
 * The Header component
 */
function Header({
  title,
  description,
  logo,
  callCenterNumber,
  callCenterHours,
}: HeaderProps) {
  return (
    <>
      <div className={styles.logo}>
        <img className={styles.logoImg} src={logo} alt="Workspace Logo" />
      </div>
      <div>
        <h1 className={styles.title}>{title}</h1>
      </div>
      <div className={styles.description}>{description}</div>

      {callCenterNumber && (
        <p className={styles.phoneNumber}>
          Call us to get started.
          <br />
          {callCenterHours}
          <br />
          <StyledLink href={`tel:${callCenterNumber}`} target="_self">
            {formatUSPhoneNumber(callCenterNumber)}
          </StyledLink>
        </p>
      )}
    </>
  )
}
